import React, { useState } from 'react';
import { Zap, Check, X, Clock } from 'lucide-react';

const HappeningNowBar = ({ 
  activityTypes = [], 
  activityStatus = {}, 
  onToggle,
  happeningNowEvents = [],
  onConfirmEvent,
  isDemo = false 
}) => {
  const [confirmingEventId, setConfirmingEventId] = useState(null);
  
  // Only show activities that are configured in admin
  if (activityTypes.length === 0) return null;

  const getActivityIcon = (type) => {
    const icons = {
      harvesting: '🍯',
      feeding: '🍽️',
      mites: '🐛',
      flooding: '💧',
      dearth: '🏜️',
      requeening: '👑',
      treating: '💊',
      supering: '📦',
    };
    return type.icon || icons[type.field_name?.toLowerCase()] || '📍';
  };

  const getActivityColor = (type, isActive, needsConfirmation = false) => {
    // Softer, more muted colors for a less bold appearance
    const colors = {
      amber: isActive ? 'bg-amber-200 border-amber-300' : 'bg-amber-50 border-amber-100',
      blue: isActive ? 'bg-blue-200 border-blue-300' : 'bg-blue-50 border-blue-100',
      red: isActive ? 'bg-red-200 border-red-300' : 'bg-red-50 border-red-100',
      green: isActive ? 'bg-green-200 border-green-300' : 'bg-green-50 border-green-100',
      purple: isActive ? 'bg-purple-200 border-purple-300' : 'bg-purple-50 border-purple-100',
      orange: isActive ? 'bg-orange-200 border-orange-300' : 'bg-orange-50 border-orange-100',
      teal: isActive ? 'bg-teal-200 border-teal-300' : 'bg-teal-50 border-teal-100',
    };
    
    // If needs confirmation, add a pulsing effect
    if (needsConfirmation) {
      return `${colors[type.color] || colors.amber} animate-pulse`;
    }
    
    return colors[type.color] || colors.amber;
  };

  // Find event needing confirmation for a given field
  const getEventNeedingConfirmation = (fieldName) => {
    return happeningNowEvents.find(
      e => e.field_name === fieldName && e.needs_confirmation && e.is_active
    );
  };

  // Check if any events need confirmation
  const eventsNeedingConfirmation = happeningNowEvents.filter(e => e.needs_confirmation && e.is_active);

  const handleConfirm = async (eventId, stillActive) => {
    await onConfirmEvent?.(eventId, stillActive);
    setConfirmingEventId(null);
  };

  return (
    <div 
      className="fixed bottom-0 left-0 lg:left-56 right-0 bg-white border-t border-stone-200 px-2 sm:px-4 py-2 sm:py-3 flex items-center gap-2 sm:gap-4 z-40 overflow-x-auto"
      data-testid="happening-now-bar"
    >
      <div className="flex items-center gap-1 sm:gap-2 text-stone-600 flex-shrink-0">
        <Zap className="h-4 w-4 text-amber-600" />
        <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Happening Now</span>
        <span className="text-xs text-stone-400 hidden sm:inline">(tap to share)</span>
        {eventsNeedingConfirmation.length > 0 && (
          <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse">
            {eventsNeedingConfirmation.length}
          </span>
        )}
      </div>
      
      <div className="flex items-center gap-1 sm:gap-2 flex-nowrap overflow-x-auto pb-1">
        {activityTypes.map((type) => {
          const isActive = activityStatus[type.field_name];
          const eventNeedingConfirmation = getEventNeedingConfirmation(type.field_name);
          const needsConfirmation = !!eventNeedingConfirmation;
          const colorClass = getActivityColor(type, isActive, needsConfirmation);
          const isConfirming = confirmingEventId === eventNeedingConfirmation?.id;
          
          // Show inline confirmation UI for this activity
          if (needsConfirmation && isConfirming) {
            return (
              <div
                key={type.field_name}
                className="flex items-center gap-2 bg-amber-100 border border-amber-300 px-3 py-2 rounded-lg"
                data-testid={`activity-confirm-${type.field_name}`}
              >
                <span className="text-sm font-medium text-stone-700">
                  <span className="mr-1">{getActivityIcon(type)}</span>
                  Still {type.name}?
                </span>
                <button
                  onClick={() => handleConfirm(eventNeedingConfirmation.id, true)}
                  className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                  data-testid={`confirm-yes-${type.field_name}`}
                >
                  <Check className="h-3 w-3" /> Yes
                </button>
                <button
                  onClick={() => handleConfirm(eventNeedingConfirmation.id, false)}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                  data-testid={`confirm-no-${type.field_name}`}
                >
                  <X className="h-3 w-3" /> No
                </button>
              </div>
            );
          }
          
          // Show button with confirmation indicator if needed
          return (
            <button
              key={type.field_name}
              onClick={() => {
                if (isDemo) return;
                if (needsConfirmation) {
                  setConfirmingEventId(eventNeedingConfirmation.id);
                } else {
                  onToggle?.(type);
                }
              }}
              disabled={isDemo}
              className={`${colorClass} border px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-stone-700 transition-all whitespace-nowrap flex-shrink-0 ${
                isDemo ? 'cursor-not-allowed opacity-60' : 'hover:scale-105 cursor-pointer'
              } ${isActive ? 'shadow-sm ring-1 ring-offset-1 ring-stone-300' : ''} ${
                needsConfirmation ? 'ring-2 ring-amber-500 ring-offset-1' : ''
              }`}
              data-testid={`activity-${type.field_name}`}
              title={needsConfirmation ? `Click to confirm if ${type.name} is still happening` : `Click to ${isActive ? 'stop' : 'start'} ${type.name}`}
            >
              <span className="mr-0.5 sm:mr-1">{getActivityIcon(type)}</span>
              <span className="hidden sm:inline">{type.name}</span>
              {isActive && !needsConfirmation && <span className="ml-0.5 sm:ml-1">✓</span>}
              {needsConfirmation && (
                <span className="ml-0.5 sm:ml-1 inline-flex items-center">
                  <Clock className="h-3 w-3 text-amber-600" />
                </span>
              )}
            </button>
          );
        })}
      </div>
      
      {/* Spacer to keep buttons away from Emergent tag */}
      <div className="flex-shrink-0 w-40"></div>
    </div>
  );
};

export default HappeningNowBar;
